import { useEffect, useRef, useState } from 'react';
import { MAX_CHAT_LENGTH } from '../lib/peer.ts';
import type { ChatLine } from '../lib/session.ts';
import { Icon } from './Icon.tsx';

interface ChatPanelProps {
  lines: ChatLine[];
  enabled: boolean;
  onSend: (text: string) => void;
  onClose?: () => void;
}

/** Textchat – läuft Peer-to-Peer über einen WebRTC-DataChannel, nicht über den Server. */
export function ChatPanel({ lines, enabled, onSend, onClose }: ChatPanelProps) {
  const [draft, setDraft] = useState('');
  const listRef = useRef<HTMLOListElement>(null);

  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [lines]);

  const submit = () => {
    if (!draft.trim()) return;
    onSend(draft);
    setDraft('');
  };

  return (
    <section className="chat-panel" aria-label="Textchat">
      <header className="chat-panel__header">
        <span>
          <Icon name="chat" size={18} /> Chat
        </span>
        {onClose && (
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Chat schließen"
          >
            <Icon name="close" />
          </button>
        )}
      </header>
      <ol ref={listRef} className="chat-panel__lines" aria-live="polite">
        {lines.length === 0 && (
          <li className="chat-panel__empty">
            {enabled
              ? 'Sag einfach Hallo.'
              : 'Sobald ihr verbunden seid, könnt ihr hier schreiben.'}
          </li>
        )}
        {lines.map((line) => (
          <li key={line.id} className={`bubble bubble--${line.from}`}>
            {line.text}
          </li>
        ))}
      </ol>
      <form
        className="chat-panel__form"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            // Ausdrücklich statt nur implizit über das Formular – manche Handy-Tastaturen senden sonst nicht.
            if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
              event.preventDefault();
              submit();
            }
          }}
          maxLength={MAX_CHAT_LENGTH}
          placeholder={enabled ? 'Schreib etwas' : 'Noch nicht verbunden'}
          disabled={!enabled}
          aria-label="Nachricht"
          enterKeyHint="send"
          autoComplete="off"
        />
        <button
          type="submit"
          className="icon-button icon-button--accent"
          disabled={!enabled || !draft.trim()}
          aria-label="Senden"
        >
          <Icon name="send" size={18} />
        </button>
      </form>
    </section>
  );
}
